import { c as _c } from "react/compiler-runtime";
import { useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useApiFetcher } from "../ApiProvider";
import { QUERY_KEYS } from "../queryKeys";
import { getCalendarConnection } from "../utils/calendarConnection";
import { useCalendarSettings } from "./useCalendarSettings";
interface CalendarEventOptions {
  refetchInterval?: number;
  staleTime?: number;
  gcTime?: number;
  refetchOnWindowFocus?: boolean;
  enabled?: boolean;
  source?: string;
}
interface MutationCallbacks {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

/**
 * Fetches calendar events for the active user.
 * Defaults to Google if no source is provided.
 */
export function useCalendarEvents(options) {
  const $ = _c(19);
  const apiFetcher = useApiFetcher();
  const {
    data: calendarSettings,
    isLoading: isCalendarSettingsLoading,
    isError: isConnectionError,
    error: connectionError
  } = useCalendarSettings();
  let t0;
  if ($[0] !== calendarSettings) {
    t0 = getCalendarConnection(calendarSettings);
    $[0] = calendarSettings;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const {
    hasCalendar,
    primarySource
  } = t0;
  const source = options?.source ?? primarySource ?? "google";
  const enabledByConnection = !isCalendarSettingsLoading && hasCalendar;
  const enabled = options?.enabled === true ? true : options?.enabled === false ? false : enabledByConnection;
  let t1;
  if ($[2] !== source) {
    t1 = [...QUERY_KEYS.CALENDAR_EVENTS, source];
    $[2] = source;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== apiFetcher || $[5] !== source) {
    t2 = () => apiFetcher(`/api/v1/user/calendar/${source}/events`);
    $[4] = apiFetcher;
    $[5] = source;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const t3 = options?.refetchInterval ?? 300000;
  const t4 = options?.staleTime ?? 300000;
  const t5 = options?.gcTime ?? 600000;
  const t6 = options?.refetchOnWindowFocus ?? true;
  let t7;
  if ($[7] !== enabled || $[8] !== t1 || $[9] !== t2 || $[10] !== t3 || $[11] !== t4 || $[12] !== t5 || $[13] !== t6) {
    t7 = {
      queryKey: t1,
      queryFn: t2,
      refetchInterval: t3,
      staleTime: t4,
      gcTime: t5,
      refetchOnWindowFocus: t6,
      enabled
    };
    $[7] = enabled;
    $[8] = t1;
    $[9] = t2;
    $[10] = t3;
    $[11] = t4;
    $[12] = t5;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const query = useQuery(t7);
  let t8;
  if ($[15] !== connectionError || $[16] !== isConnectionError || $[17] !== query) {
    t8 = Object.assign(query, {
      isConnectionError,
      connectionError: connectionError as Error | null
    });
    $[15] = connectionError;
    $[16] = isConnectionError;
    $[17] = query;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  return t8;
}

/** @deprecated Use useCalendarEvents instead */
export const useGoogleCalendarEvents = useCalendarEvents;
export function useCreateCalendarEvent(t0) {
  const $ = _c(8);
  const source = t0 === undefined ? "google" : t0;
  const apiFetcher = useApiFetcher();
  const queryClient = useQueryClient();
  let t1;
  if ($[0] !== apiFetcher || $[1] !== source) {
    t1 = event => apiFetcher(`/api/v1/user/calendar/${source}/events`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify(event)
    });
    $[0] = apiFetcher;
    $[1] = source;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== queryClient) {
    t2 = () => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.CALENDAR_EVENTS
      });
    };
    $[3] = queryClient;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t1 || $[6] !== t2) {
    t3 = {
      mutationFn: t1,
      onSuccess: t2
    };
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return useMutation(t3);
}

/** @deprecated Use useCreateCalendarEvent instead */
export const useCreateGoogleCalendarEvent = useCreateCalendarEvent;
export function useUpdateCalendarEvent(t0) {
  const $ = _c(8);
  const source = t0 === undefined ? "google" : t0;
  const apiFetcher = useApiFetcher();
  const queryClient = useQueryClient();
  let t1;
  if ($[0] !== apiFetcher || $[1] !== source) {
    t1 = t2 => {
      const {
        eventId,
        event
      } = t2;
      return apiFetcher(`/api/v1/user/calendar/${source}/events/${eventId}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(event)
      });
    };
    $[0] = apiFetcher;
    $[1] = source;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== queryClient) {
    t2 = () => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.CALENDAR_EVENTS
      });
    };
    $[3] = queryClient;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t1 || $[6] !== t2) {
    t3 = {
      mutationFn: t1,
      onSuccess: t2
    };
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return useMutation(t3);
}

/** @deprecated Use useUpdateCalendarEvent instead */
export const useUpdateGoogleCalendarEvents = useUpdateCalendarEvent;
export function useDeleteCalendarEvent(callbacks, t0) {
  const $ = _c(12);
  const source = t0 === undefined ? "google" : t0;
  const apiFetcher = useApiFetcher();
  const queryClient = useQueryClient();
  let t1;
  if ($[0] !== apiFetcher || $[1] !== source) {
    t1 = eventId => apiFetcher(`/api/v1/user/calendar/${source}/events/${eventId}`, {
      method: "DELETE"
    });
    $[0] = apiFetcher;
    $[1] = source;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== callbacks || $[4] !== queryClient) {
    t2 = () => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.CALENDAR_EVENTS
      });
      callbacks?.onSuccess?.();
    };
    $[3] = callbacks;
    $[4] = queryClient;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== callbacks) {
    t3 = error => {
      callbacks?.onError?.(error);
    };
    $[6] = callbacks;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== t1 || $[9] !== t2 || $[10] !== t3) {
    t4 = {
      mutationFn: t1,
      onSuccess: t2,
      onError: t3
    };
    $[8] = t1;
    $[9] = t2;
    $[10] = t3;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  return useMutation(t4);
}

/** @deprecated Use useDeleteCalendarEvent instead */
export const useDeleteGoogleCalendarEvent = useDeleteCalendarEvent;